import { Button, CanAccess, IconButton, Loader, OverviewCard, Page, Sidebar } from "@eisox/design-system";
import {
  GatewayIcon,
  NutIcon,
  PanelLeftIcon,
  PlanIcon,
  RoomIcon,
  ThermometerIcon,
  TickIcon,
  ValveIcon,
  WarningIcon,
} from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { PlanCardLink, PresenceCard, TablePlanningsStat, TemperaturesCard } from "~/features/dashboard";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { isPresenceRecentlyDetected } from "~/helpers/building.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { calculateAverageComfort } from "~/helpers/room.helper";
import { useUserPreferenceStore } from "~/integrations/zustand/stores/user-preference.store";
import { m } from "~/paraglide/messages.js";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/dashboard")({
  head: ({ match: { context }, params: { buildingId } }) =>
    buildingPageHead(context.queryClient, buildingId, m.dashboard()),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const advancedMaintenance = useUserPreferenceStore(s => s.maintenance.advanced);

  const { permissions, building, floors, rooms, valves, gateways, date } = useBuildingContext(Route.id);

  const floorId = floors.find(f => f.id)?.id;

  const { data: plan } = useQuery({ ...floorPlanQueries.byId(floorId ?? ""), enabled: !!floorId });

  const {
    data: weather,
    isLoading: isWeatherLoading,
    isError: isWeatherError,
  } = useQuery({
    ...buildingApi.queries.weather(building.id),
    retry: false,
    meta: { skipGlobalLoader: true },
  });

  const { data: planningsStat, isLoading: isPlanningsStatLoading } = useQuery({
    ...buildingApi.queries.planningsStat(building.id),
    retry: false,
    meta: { skipGlobalLoader: true },
  });

  const presence = isPresenceRecentlyDetected(building.lastPresenceDetected, date);
  const comfortAverage = rooms.length > 0 ? calculateAverageComfort(rooms) : undefined;

  const valvesProblemsCount = valves.reduce((acc, v) => acc + (v.errors.length > 0 ? 1 : 0), 0);
  const gatewaysProblemsCount = gateways.reduce((acc, g) => acc + (g.errors.length > 0 ? 1 : 0), 0);

  const errorsCount = valvesProblemsCount + gatewaysProblemsCount;

  return (
    <Page.Root>
      <Page.Header>
        {isMobile && (
          <Sidebar.Trigger asChild>
            <IconButton>
              <PanelLeftIcon />
            </IconButton>
          </Sidebar.Trigger>
        )}
        <Page.Title>{building.name}</Page.Title>
        <CanAccess isAuthorized={permissions.page?.settings}>
          {!isMobile && (
            <Button asChild>
              <Link to={"/buildings/$buildingId/settings/buildings/general"} params={{ buildingId: building.id }}>
                {m.settings()} <NutIcon />
              </Link>
            </Button>
          )}
        </CanAccess>
      </Page.Header>
      <Page.Content className="overflow-y-auto bg-transparent" data-testid="Dashboard">
        <div className="md:gap-7.5 gap-2.5 grid h-full grid-cols-[repeat(auto-fit,minmax(max(200px,(100%-3*30px)/4),1fr))] grid-rows-[min-content] [direction:rtl] *:w-full *:[direction:ltr]">
          <div className="md:gap-7.5 gap-2.5 col-span-2 flex flex-wrap *:flex-1">
            <CanAccess isAuthorized={permissions.page?.maintenance}>
              <OverviewCard
                className="flex-1"
                icon={errorsCount > 0 ? <WarningIcon /> : <TickIcon />}
                color={errorsCount > 0 ? "red" : "green"}
                title={m.error_count({ count: errorsCount })}
                subtitle={m.dashboard_maintenance()}
                variants={errorsCount > 0 ? "danger" : "success"}
                size={"large"}
                onClick={() => {
                  void navigate({
                    to:
                      advancedMaintenance && !isMobile
                        ? "/buildings/$buildingId/maintenance/advanced"
                        : "/buildings/$buildingId/maintenance",
                    params: { buildingId: building.id },
                  });
                }}
              />
            </CanAccess>
            {isWeatherLoading ? (
              <Loader className="self-center" />
            ) : (
              <TemperaturesCard className="flex-1" temperatures={!isWeatherError && weather ? weather : []} />
            )}
          </div>
          <div className="p-2.5 md:gap-7.5 md:p-7.5 md:*:gap-7.5 gap-2.5 *:gap-2.5 col-span-2 flex flex-wrap items-stretch rounded-[10px] bg-primary-white *:flex *:flex-1 *:items-center [&>*>*]:h-full [&>*>*]:flex-1">
            <div>
              <OverviewCard
                icon={<PlanIcon />}
                title={floors.length.toString()}
                subtitle={m.floor_count({ count: floors.length })}
                variants="info"
                size="small"
              />
              <OverviewCard
                icon={<RoomIcon />}
                title={rooms.length.toString()}
                subtitle={m.room_count({ count: rooms.length })}
                variants="info"
                size="small"
              />
            </div>
            <div>
              <CanAccess isAuthorized={permissions.gateway?.read}>
                <OverviewCard
                  icon={<GatewayIcon />}
                  title={gateways.length.toString()}
                  subtitle={m.gateway_count({ count: gateways.length })}
                  variants="info"
                  size="small"
                />
              </CanAccess>
              <OverviewCard
                icon={<ValveIcon />}
                title={valves.length.toString()}
                subtitle={m.valve_count({ count: valves.length })}
                variants="info"
                size="small"
              />
            </div>
          </div>
          <PlanCardLink
            className="col-span-2 row-span-2"
            plan={plan}
            to="/buildings/$buildingId/floors"
            params={{ buildingId: building.id }}
          />

          <div className="md:bg-primary-white md:col-span-2 md:flex md:items-center md:justify-center md:rounded-[10px] md:p-10 hidden">
            {isPlanningsStatLoading || !planningsStat ? (
              <Loader className="self-center" />
            ) : (
              <TablePlanningsStat data={planningsStat} />
            )}
          </div>

          <div className="md:gap-7.5 gap-2.5 col-span-2 flex flex-wrap *:flex-1">
            <OverviewCard
              className="flex-1"
              icon={<ThermometerIcon />}
              color="white"
              iconBackgroundColor="orange"
              title={comfortAverage?.toString() ?? "-"}
              subtitle={m.temperature_average_comfort()}
              hasDegre
              size="large"
            />
            <CanAccess isAuthorized={permissions.building?.lastPresenceDetected?.read}>
              <PresenceCard className="flex-1" presence={presence} />
            </CanAccess>
          </div>
        </div>
      </Page.Content>
    </Page.Root>
  );
}
